'use client';

/**
 * Activity Feed Component
 *
 * Real-time event stream with configurable activity types
 * Inspired by GitHub Activity + Stripe Events
 *
 * @example
 * <ActivityFeed
 *   activities={[
 *     { id: '1', type: 'user_created', message: 'New user joined', timestamp: new Date() },
 *   ]}
 *   activityConfig={{
 *     user_created: { icon: UserPlus, color: 'text-green-400', bg: 'bg-green-400/10' },
 *   }}
 * />
 */

import React from 'react';
import {
  UserPlus,
  Shield,
  Rocket,
  Key,
  Bot,
  AlertTriangle,
  Zap,
  Activity,
  Settings,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  CreditCard,
  Bell,
  LucideIcon,
} from 'lucide-react';

// Built-in activity types
export type BuiltInActivityType =
  | 'user_created'
  | 'user_deleted'
  | 'role_modified'
  | 'app_deployed'
  | 'api_key_generated'
  | 'api_key_revoked'
  | 'agent_executed'
  | 'agent_failed'
  | 'alert_triggered'
  | 'alert_resolved'
  | 'gateway_spike'
  | 'settings_changed'
  | 'subscription_created'
  | 'subscription_upgraded'
  | 'subscription_cancelled'
  | 'payment_succeeded'
  | 'payment_failed'
  | 'reminder_sent'
  | 'generic';

export interface ActivityConfigItem {
  icon: LucideIcon;
  color: string;
  bg: string;
}

export interface ActivityItem<T extends string = BuiltInActivityType> {
  id: string;
  type: T;
  message: string;
  timestamp: Date;
  app?: string;
  user?: string;
  metadata?: Record<string, unknown>;
}

export interface ActivityFeedProps<T extends string = BuiltInActivityType> {
  activities: ActivityItem<T>[];
  maxItems?: number;
  onViewAll?: () => void;
  onItemClick?: (activity: ActivityItem<T>) => void;
  title?: string;
  showAutoRefresh?: boolean;
  activityConfig?: Partial<Record<T, ActivityConfigItem>>;
}

// Default activity config
const defaultActivityConfig: Record<BuiltInActivityType, ActivityConfigItem> = {
  user_created: { icon: UserPlus, color: 'text-green-400', bg: 'bg-green-400/10' },
  user_deleted: { icon: Trash2, color: 'text-red-400', bg: 'bg-red-400/10' },
  role_modified: { icon: Shield, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  app_deployed: { icon: Rocket, color: 'text-violet-400', bg: 'bg-violet-400/10' },
  api_key_generated: { icon: Key, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  api_key_revoked: { icon: Key, color: 'text-red-400', bg: 'bg-red-400/10' },
  agent_executed: { icon: Bot, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
  agent_failed: { icon: Bot, color: 'text-red-400', bg: 'bg-red-400/10' },
  alert_triggered: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-400/10' },
  alert_resolved: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-400/10' },
  gateway_spike: { icon: Zap, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  settings_changed: { icon: Settings, color: 'text-slate-400', bg: 'bg-slate-400/10' },
  subscription_created: { icon: CreditCard, color: 'text-green-400', bg: 'bg-green-400/10' },
  subscription_upgraded: { icon: Zap, color: 'text-violet-400', bg: 'bg-violet-400/10' },
  subscription_cancelled: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10' },
  payment_succeeded: { icon: CreditCard, color: 'text-green-400', bg: 'bg-green-400/10' },
  payment_failed: { icon: CreditCard, color: 'text-red-400', bg: 'bg-red-400/10' },
  reminder_sent: { icon: Bell, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  generic: { icon: Activity, color: 'text-slate-400', bg: 'bg-slate-400/10' },
};

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function ActivityRow<T extends string>({
  activity,
  config,
  onClick
}: {
  activity: ActivityItem<T>;
  config: ActivityConfigItem;
  onClick?: () => void;
}) {
  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left group ${
        onClick ? 'hover:bg-white/5 cursor-pointer' : ''
      }`}
    >
      <div className={`h-8 w-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`h-4 w-4 ${config.color}`} />
      </div>

      <div className="flex-1 min-w-0">
        <p className={`text-sm text-white/90 ${onClick ? 'group-hover:text-white' : ''} transition-colors`}>
          {activity.message}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {activity.app && (
            <span className="text-xs text-violet-400 font-medium">{activity.app}</span>
          )}
          <span className="text-xs text-white/40">
            {formatTimeAgo(new Date(activity.timestamp))}
          </span>
        </div>
      </div>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center mb-3">
        <Activity className="h-5 w-5 text-white/30" />
      </div>
      <p className="text-sm text-white/50">No recent activity</p>
      <p className="text-xs text-white/30 mt-1">Events will appear here as they happen</p>
    </div>
  );
}

export function ActivityFeed<T extends string = BuiltInActivityType>({
  activities,
  maxItems = 10,
  onViewAll,
  onItemClick,
  title = 'Live Activity',
  showAutoRefresh = true,
  activityConfig,
}: ActivityFeedProps<T>) {
  const displayActivities = activities.slice(0, maxItems);

  const getConfig = (type: T): ActivityConfigItem => {
    // Check custom config first
    if (activityConfig?.[type]) {
      return activityConfig[type]!;
    }
    // Fall back to default config
    if (type in defaultActivityConfig) {
      return defaultActivityConfig[type as BuiltInActivityType];
    }
    // Ultimate fallback
    return defaultActivityConfig.generic;
  };

  return (
    <div className="bg-slate-900/60 backdrop-blur-lg border border-white/10 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
          <h3 className="text-sm font-semibold text-white">{title}</h3>
        </div>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="text-xs text-white/50 hover:text-white/80 transition-colors"
          >
            View All →
          </button>
        )}
      </div>

      {/* Activity list */}
      <div className="max-h-[400px] overflow-y-auto">
        {displayActivities.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="p-2 space-y-1">
            {displayActivities.map((activity) => (
              <ActivityRow
                key={activity.id}
                activity={activity}
                config={getConfig(activity.type)}
                onClick={onItemClick ? () => onItemClick(activity) : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer with refresh indicator */}
      {showAutoRefresh && (
        <div className="px-4 py-2 border-t border-white/5 flex items-center justify-center gap-2">
          <RefreshCw className="h-3 w-3 text-white/30 animate-spin" style={{ animationDuration: '3s' }} />
          <span className="text-xs text-white/30">Auto-refreshing</span>
        </div>
      )}
    </div>
  );
}
